import axios, { AxiosRequestConfig } from 'axios';
import { AuthenticationProvider } from '@/modules/shared/bridges/AuthenticationProvider';

/**
 * Kapselt das Aufrufmuster der APRO.CON-Servicefassade an einer Stelle:
 *   POST {serviceUrl}/{area}/{action}   Body { SID, UserID?, Filiale?, ...params }
 *   Antwort immer HTTP 200, Nutzdaten unter data.{action}_response,
 *   Fehler unter data.{action}_response.ServiceErrors[]
 */

/**
 * Fehlertext aus einem ServiceErrors-Eintrag. Die Fassade füllt mal die Empfehlung, mal die
 * Beschreibung, und schreibt die Felder mal groß (ERRORRECOMMENDATION), mal gemischt
 * (ErrorRecommendation). Empfehlung zuerst, weil sie die für Nutzer brauchbarere Meldung ist.
 */
export function describeServiceError(error: any): string {
    return error?.ERRORRECOMMENDATION
        || error?.ErrorRecommendation
        || error?.ERRORDESCRIPTION
        || error?.ErrorDescription
        || 'Unbekannter Fehler der Servicefassade.';
}

/** Fachlicher Fehler aus ServiceErrors — die Fassade liefert ihn mit HTTP 200. */
export class ServiceError extends Error {
    public readonly action: string;
    public readonly raw: unknown;

    constructor(message: string, action: string, raw: unknown) {
        super(message);
        this.name = 'ServiceError';
        this.action = action;
        this.raw = raw;
        // notwendig, damit instanceof über die TS-Klassenhierarchie funktioniert
        Object.setPrototypeOf(this, ServiceError.prototype);
    }
}

/** Ersetzt das bisherige `return Promise.reject()` ohne Grund. */
export class NotAuthenticatedError extends Error {
    public readonly action: string;

    constructor(action: string) {
        super(`Kein gültiges SID für "${action}" vorhanden.`);
        this.name = 'NotAuthenticatedError';
        this.action = action;
        Object.setPrototypeOf(this, NotAuthenticatedError.prototype);
    }
}

export interface CallOptions {
    /**
     * SID mitschicken und vorher prüfen (Standard: true). Nur für Aufrufe, die auch
     * ohne Anmeldung funktionieren müssen, z.B. getLicenceInformation auf /About.
     */
    withSid?: boolean;
    /**
     * `UserID` mitschicken — nur get_connection_status und get_POSManagerReports.
     * Achtung: get_URL_POSManagerKassenbericht und get_URL_POSManagerBon erwarten
     * `UserId` (kleines d) und müssen das Feld selbst über `params` setzen.
     */
    withUserId?: boolean;
    /** Filiale mitschicken — die Mehrheit der Endpunkte erwartet sie. */
    withBranchId?: boolean;
    /** Überschreibt das globale Timeout aus VUE_APP_AXIOS_TIMEOUT (z.B. Reports: 120000). */
    timeoutMs?: number;
    /** Unabhängig von timeoutMs setzbar — mehrere Aufrufe nutzen nur die eigene Meldung. */
    timeoutMessage?: string;
    /** Nur setzen, wenn der Envelope-Key ausnahmsweise nicht `{action}_response` lautet. */
    responseKey?: string;
}

export class ServiceClient {
    private readonly auth: AuthenticationProvider;
    private readonly area: string;

    /** @param area Fassadenbereich, z.B. 'POSManager', 'Kundenkarten', 'Authentication'. */
    constructor(auth: AuthenticationProvider, area: string) {
        this.auth = auth;
        this.area = area;
    }

    public async call<T>(
        action: string,
        params: Record<string, unknown> = {},
        options: CallOptions = {},
    ): Promise<T> {
        const withSid = options.withSid !== false;
        const sid = withSid ? this.auth.getAuthToken() : undefined;
        if (withSid && !sid) {
            throw new NotAuthenticatedError(action);
        }

        const payload: Record<string, unknown> = {
            ...(withSid ? { SID: sid } : {}),
            ...(options.withUserId ? { UserID: this.auth.getUserId() } : {}),
            ...(options.withBranchId ? { Filiale: this.auth.getBranchId() } : {}),
            ...params,
        };

        const config: AxiosRequestConfig = {};
        if (options.timeoutMs !== undefined) {
            config.timeout = options.timeoutMs;
        }
        if (options.timeoutMessage !== undefined) {
            config.timeoutErrorMessage = options.timeoutMessage;
        }

        const url = `${this.auth.getServiceUrl()}/${this.area}/${action}`;
        const response = await axios.post(url, payload, config);

        // Ein einziger Ort, an dem der Envelope-Key aus dem Aktionsnamen abgeleitet wird.
        const envelope = response.data?.[options.responseKey ?? `${action}_response`];

        const errors = envelope?.ServiceErrors;
        if (Array.isArray(errors) && errors.length > 0) {
            throw new ServiceError(describeServiceError(errors[0]), action, errors[0]);
        }

        // envelope darf null sein — set_Filiale signalisiert Erfolg genau so.
        return envelope as T;
    }
}
